/**
 * Optimizely Web Experimentation event tracking utilities.
 *
 * Provides a safe, SSR-compatible way to push custom events into the
 * Optimizely Web Experimentation data layer (window['optimizely']).
 */
import {trackFxEvent} from './optimizely-fx-events'

const stripNullish = (obj) => {
    if (!obj || typeof obj !== 'object') return undefined
    const cleaned = {}
    for (const [key, val] of Object.entries(obj)) {
        if (val !== undefined && val !== null) {
            cleaned[key] = val
        }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

/**
 * Push a custom event to the Optimizely Web Experimentation data layer.
 *
 * @param {Object} params
 * @param {string} params.eventName - Required Optimizely event name (e.g. "add_to_bag").
 * @param {Object} [params.tags] - Optional tags object (revenue, value).
 * @param {Object} [params.properties] - Optional properties object (source, Category, SKU, etc.).
 */
export const trackOptimizelyEvent = ({eventName, tags, properties}) => {
    if (typeof window === 'undefined') return

    try {
        window['optimizely'] = window['optimizely'] || []

        const payload = {
            type: 'event',
            eventName
        }

        const cleanTags = stripNullish(tags)
        if (cleanTags) {
            payload.tags = cleanTags
        }

        const cleanProps = stripNullish(properties)
        if (cleanProps) {
            payload.properties = cleanProps
        }

        window['optimizely'].push(payload)
    } catch (error) {
        console.error('[Optimizely Web Exp] Failed to track event:', eventName, error)
    }

    // Mirror into Feature Experimentation as a conversion event; reserved tags
    // (revenue, value) win over properties on collision. Never throws.
    trackFxEvent(eventName, {...properties, ...tags})
}

/**
 * Track an "add_to_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 */
export const trackAddToBagEvent = ({sku, variationGroupId, value, category, source} = {}) => {
    trackOptimizelyEvent({
        eventName: 'add_to_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "remove_from_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 */
export const trackRemoveFromBagEvent = ({sku, variationGroupId, value, category, source} = {}) => {
    trackOptimizelyEvent({
        eventName: 'remove_from_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "add_to_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackAddToWishlistEvent = ({sku, variationGroupId, value, category, source} = {}) => {
    trackOptimizelyEvent({
        eventName: 'add_to_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "remove_from_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackRemoveFromWishlistEvent = ({
    sku,
    variationGroupId,
    value,
    category,
    source
} = {}) => {
    trackOptimizelyEvent({
        eventName: 'remove_from_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "RPV" event with explicit parameters.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.subcategory] - Product subcategory.
 * @param {number} [params.quantity] - Product quantity.
 */
export const trackRPVEvent = ({sku, value, category, subcategory, quantity} = {}) => {
    trackOptimizelyEvent({
        eventName: 'rpv',
        tags: {
            revenue: value * 100 // Optional in cents as integer (500 == $5.00)
        },
        properties: {
            SKU: sku,
            Category: category,
            Subcategory: subcategory,
            Quantity: quantity
        }
    })
}

/**
 * Track a "product_purchase" event for each purchased item.
 *
 * @param {Array<Object>} products
 * @param {string|number} [products[].productId] - Product ID / SKU.
 * @param {string|number} [products[].variationGroupId] - Variation group identifier.
 * @param {number} [products[].value] - Product value.
 * @param {string} [products[].category] - Product category.
 * @param {number} [products[].quantity] - Purchased quantity.
 */
export const trackProductPurchasesEvent = (products = []) => {
    if (!Array.isArray(products)) return

    products.forEach(({productId, category, variationGroupId, value, quantity, Quantity}) => {
        trackOptimizelyEvent({
            eventName: 'product_purchase',
            tags: {
                value
            },
            properties: {
                SKU: productId,
                Category: category,
                variationGroupId,
                Quantity: quantity ?? Quantity
            }
        })
    })
}
